import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import useBodyScroll from '../hooks/useBodyScroll';
import TabNav from '../components/TabNav';
import { Section, Inner, fadeInUp } from '../styles/v2.styled';
import { BoardView } from '../components/board';

/* ═══════════════════════════════════════
   BoardPage (v2) — 커뮤니티 게시판
   ═══════════════════════════════════════ */

// ── Animations ──
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  33%      { transform: translateY(-10px) rotate(1deg); }
  66%      { transform: translateY(5px) rotate(-1deg); }
`;

// ── Page Wrapper ──
const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

/* TabNav/TabInner/TabLink 삭제 — 공통 TabNav 컴포넌트 사용 */

// ── Hero ──
const HeroWrapper = styled.section`
  position: relative;
  width: 100%;
  padding: 64px 0;
  overflow: hidden;
  background: linear-gradient(
    -45deg,
    #00292e,
    #003845,
    #005c68,
    #007a8a,
    #0096c7
  );
  background-size: 300% 300%;
  animation: ${gradientShift} 12s ease infinite;

  @media (max-width: 768px) {
    padding: 44px 0;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at 30% 50%,
    rgba(0, 180, 216, 0.15) 0%,
    transparent 60%
  );
  pointer-events: none;
`;

const FloatShape = styled.div`
  position: absolute;
  border-radius: 50%;
  opacity: 0.06;
  background: #fff;
  animation: ${float} ${({ $dur }) => $dur || '6s'} ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay || '0s'};
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  ${({ $top }) => $top && `top: ${$top};`}
  ${({ $left }) => $left && `left: ${$left};`}
  ${({ $right }) => $right && `right: ${$right};`}
  ${({ $bottom }) => $bottom && `bottom: ${$bottom};`}
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  border-radius: 9999px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 20px;
  animation: ${fadeInUp} 0.7s 0.1s ease both;
`;

const HeroTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.bold};
  font-size: 2.75rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  margin: 0 0 16px;
  letter-spacing: -1px;
  animation: ${fadeInUp} 0.7s 0.25s ease both;

  @media (max-width: 768px) {
    font-size: 1.85rem;
  }
`;

const HeroDesc = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.7s 0.4s ease both;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

// ── Board Content Area ──
const BoardSection = styled(Section)`
  padding: 40px 0 80px;

  @media (max-width: 768px) {
    padding: 24px 0 48px;
  }
`;

const BoardContent = styled.div`
  animation: ${fadeInUp} 0.6s ease both;
`;

// ── Tab label map (LMS 톤) ──
const TABS = [
  { type: 'NewsBoard', path: '/NewsBoard/0', label: '공지사항' },
  { type: 'FreeBoard', path: '/FreeBoard/0', label: '자유게시판' },
  { type: 'QaBoard', path: '/QaBoard/0', label: 'Q&A' },
  // DeveloperNoteBoard 테이블/라우터를 '수강후기'로 라벨링
  {
    type: 'DeveloperNoteBoard',
    path: '/DeveloperNoteBoard/0',
    label: '수강후기',
  },
];

// ── Type별 hero 라벨/문구 ──
const HERO_BY_TYPE = {
  NewsBoard: {
    eyebrow: 'NOTICE',
    title: '공지사항',
    desc: '강의·운영 관련 중요한 공지사항을 확인하세요.',
  },
  FreeBoard: {
    eyebrow: 'COMMUNITY',
    title: '자유게시판',
    desc: '수강생끼리 자유롭게 정보를 공유하는 공간입니다.',
  },
  QaBoard: {
    eyebrow: 'Q&A',
    title: '질문과 답변',
    desc: '강의 내용·실습에 대한 질문을 남기면 강사가 답변드립니다.',
  },
  DeveloperNoteBoard: {
    eyebrow: 'REVIEW',
    title: '수강 후기',
    desc: '실제 수강생들의 학습 후기와 성장 스토리를 확인하세요.',
  },
};

// ── API endpoint resolver ──
function getApiEndpoints(boardType) {
  switch (boardType) {
    case 'FreeBoard':
      return {
        apiFetchPage: '/request/SelectFreeBoardPage',
        apiFetchPageList: '/request/SelectFreeBoardPageList',
        apiReadNews: '/request/ReadFreeBoard',
        apiReadComments: '/request/ReadFreeComments',
        apiIncreaseCount: '/request/FreeSearchCountPlus',
        apiDeleteNews: '/request/DeleteFreeBoard',
        apiDeleteComment: '/request/DeleteFreeComment',
        apiInsertComment: '/request/InsertFreeComment',
        apiInsertNews: '/request/InsertFreeBoard',
        apiUpdateNews: '/request/UpdateFreeBoard',
      };
    case 'NewsBoard':
      return {
        apiFetchPage: '/request/SelectNewsBoardPage',
        apiFetchPageList: '/request/SelectNewsBoardPageList',
        apiReadNews: '/request/ReadNewsBoard',
        apiReadComments: '/request/ReadNewsComments',
        apiIncreaseCount: '/request/NewsSearchCountPlus',
        apiDeleteNews: '/request/DeleteNewsBoard',
        apiDeleteComment: '/request/DeleteNewsComment',
        apiInsertComment: '/request/InsertNewsComment',
        apiInsertNews: '/request/InsertNewsBoard',
        apiUpdateNews: '/request/UpdateNewsBoard',
      };
    case 'QaBoard':
      return {
        apiFetchPage: '/request/SelectQaBoardPage',
        apiFetchPageList: '/request/SelectQaBoardPageList',
        apiReadNews: '/request/ReadQaBoard',
        apiReadComments: '/request/ReadQaComments',
        apiIncreaseCount: '/request/QaSearchCountPlus',
        apiDeleteNews: '/request/DeleteQaBoard',
        apiDeleteComment: '/request/DeleteQaComment',
        apiInsertComment: '/request/InsertQaComment',
        apiInsertNews: '/request/InsertQaBoard',
        apiUpdateNews: '/request/UpdateQaBoard',
      };
    case 'DeveloperNoteBoard':
      return {
        apiFetchPage: '/request/SelectDeveloperNoteBoardPage',
        apiFetchPageList: '/request/SelectDeveloperNoteBoardPageList',
        apiReadNews: '/request/ReadDeveloperNoteBoard',
        apiReadComments: '/request/ReadDeveloperNoteComments',
        apiIncreaseCount: '/request/DeveloperNoteSearchCountPlus',
        apiDeleteNews: '/request/DeleteDeveloperNoteBoard',
        apiDeleteComment: '/request/DeleteDeveloperNoteComment',
        apiInsertComment: '/request/InsertDeveloperNoteComment',
        apiInsertNews: '/request/InsertDeveloperNoteBoard',
        apiUpdateNews: '/request/UpdateDeveloperNoteBoard',
      };
    default:
      return {
        apiFetchPage: '',
        apiFetchPageList: '',
        apiReadNews: '',
        apiReadComments: '',
        apiIncreaseCount: '',
        apiDeleteNews: '',
        apiDeleteComment: '',
        apiInsertComment: '',
        apiInsertNews: '',
        apiUpdateNews: '',
      };
  }
}

function BoardPage(props) {
  useBodyScroll();

  // 라우터에서 넘어온 초기값
  const initialNum = props.match.params.num || 0;
  const initialType = props.Type;

  // state로 관리
  const [boardType, setBoardType] = useState(initialType);
  const [num, setNum] = useState(initialNum);

  // 현재 boardType에 맞는 API endpoints
  const apis = getApiEndpoints(boardType);

  return (
    <PageWrapper>
      {/* ── Tab Navigation ── */}
      <TabNav tabs={TABS} activeType={boardType} />

      {/* ── Hero Banner ── */}
      <HeroWrapper>
        <HeroOverlay />
        <FloatShape $size="200px" $top="-30px" $right="-50px" $dur="8s" />
        <FloatShape
          $size="120px"
          $bottom="15px"
          $left="8%"
          $dur="6s"
          $delay="1s"
        />

        <HeroInner>
          <HeroEyebrow>
            <span>{(HERO_BY_TYPE[boardType] || HERO_BY_TYPE.NewsBoard).eyebrow}</span>
          </HeroEyebrow>
          <HeroTitle>{(HERO_BY_TYPE[boardType] || HERO_BY_TYPE.NewsBoard).title}</HeroTitle>
          <HeroDesc>
            {(HERO_BY_TYPE[boardType] || HERO_BY_TYPE.NewsBoard).desc}
          </HeroDesc>
        </HeroInner>
      </HeroWrapper>

      {/* ── Board Content ── */}
      <BoardSection>
        <Inner>
          <BoardContent>
            {boardType && (
              <BoardView
                key={boardType}
                apiCreateSession="/request/CreateSession"
                apiFetchPage={apis.apiFetchPage}
                apiFetchPageList={apis.apiFetchPageList}
                apiReadNews={apis.apiReadNews}
                apiReadComments={apis.apiReadComments}
                apiIncreaseCount={apis.apiIncreaseCount}
                apiDeleteNews={apis.apiDeleteNews}
                apiDeleteComment={apis.apiDeleteComment}
                apiInsertComment={apis.apiInsertComment}
                apiInsertNews={apis.apiInsertNews || `/request/Insert${boardType}`}
                apiUpdateNews={apis.apiUpdateNews || `/request/Update${boardType}`}
                setNum={setNum}
              />
            )}
          </BoardContent>
        </Inner>
      </BoardSection>
    </PageWrapper>
  );
}

export default BoardPage;
